import { ReactElement } from "react";
import { t } from "@lingui/core/macro";

import { adminUrls } from "_/routes";

import { Permission } from "_/data/rbac";
import { Machine } from "_/data/machines";
import { BuildsetIdMap } from "_/data/buildsets";
import { ChannelNameToBuildset } from "_/data/channels";
import { formatBuildsetLabel } from "_/utils/buildset-display";

import { CustomCellStatic, CustomCellLink } from "_/components/table";
import { testPermissions } from "_/components/permission-test";
import { Link } from "_/components/router";
import { BuildsetTooltip } from "_/components/buildsetTooltip";

import { Uuid } from "_/types";

import * as S from "./styled";

type BuildsetView =
  | { kind: "empty" }
  | {
      kind: "single";
      current: { value: string; id: Uuid; pinned: boolean; tooltip?: string };
    }
  | {
      kind: "pending";
      pending: { value: string; id?: Uuid; tooltip?: string };
      current: {
        value: string | null;
        id: Uuid | undefined;
        pinned: boolean;
        tooltip?: string;
      };
    };

export function getBuildsetView(props: {
  machine: Machine;
  channelNameToBuildset: ChannelNameToBuildset;
  buildsetIdMap: BuildsetIdMap;
}): BuildsetView {
  const { machine, channelNameToBuildset, buildsetIdMap } = props;
  const { hardwareAttached, buildsetPin, pinReason, updateChannel, metadata } =
    machine;

  if (!hardwareAttached) return { kind: "empty" };

  const metadataBuildsetId =
    typeof metadata?.buildsetId === "string" ? metadata.buildsetId : null;

  const reportedBuildset = metadataBuildsetId
    ? buildsetIdMap[metadataBuildsetId]
    : null;

  const targetBuildset = buildsetPin
    ? (buildsetIdMap[buildsetPin] ?? null)
    : channelNameToBuildset[updateChannel];

  const buildTooltip = ({
    label,
    pin = false,
  }: {
    label: { value: string; fullName?: string; id?: Uuid };
    pin?: boolean;
  }) => {
    if (!label.id) return undefined;
    const base =
      label.fullName && label.fullName !== label.value
        ? `${label.fullName}\n${label.id}`
        : label.id;
    if (pin && pinReason) {
      return `${base}\n${t`common.pin-reason`}: ${pinReason}`;
    }
    return base;
  };

  // Pinned machines: show pending until the reported buildset matches the pin.
  if (buildsetPin) {
    const targetLabel = formatBuildsetLabel(targetBuildset ?? undefined);
    const reportedLabel = reportedBuildset
      ? formatBuildsetLabel(reportedBuildset)
      : null;
    const targetTooltip = buildTooltip({ label: targetLabel, pin: true });
    const reportedTooltip = reportedLabel
      ? buildTooltip({ label: reportedLabel })
      : undefined;

    const targetId = targetBuildset?.id ?? buildsetPin;
    const needsPending =
      !reportedBuildset || metadataBuildsetId !== buildsetPin;

    if (needsPending) {
      return {
        kind: "pending",
        pending: {
          value: targetLabel.value,
          id: targetId,
          tooltip: targetTooltip,
        },
        current: {
          value: reportedLabel?.value ?? null,
          id: reportedBuildset?.id ?? targetId,
          pinned: true,
          tooltip: reportedTooltip,
        },
      };
    }
    // Reported buildset matches pin
    return {
      kind: "single",
      current: {
        value: targetLabel.value,
        id: targetId,
        pinned: true,
        tooltip: targetTooltip,
      },
    };
  }
  if (!targetBuildset && !reportedBuildset) return { kind: "empty" };

  // Unpinned machines: with a channel but no reported buildset
  if (updateChannel && !reportedBuildset) {
    const targetLabel = formatBuildsetLabel(targetBuildset ?? undefined);
    const targetTooltip = buildTooltip({ label: targetLabel });
    return {
      kind: "pending",
      pending: {
        value: targetLabel.value,
        id: targetBuildset?.id,
        tooltip: targetTooltip,
      },
      current: {
        value: null,
        id: targetBuildset?.id,
        pinned: false,
      },
    };
  }

  // Unpinned machines: reported buildset differs from channel target.
  if (
    updateChannel &&
    targetBuildset &&
    reportedBuildset &&
    targetBuildset.id !== reportedBuildset.id
  ) {
    const target = formatBuildsetLabel(targetBuildset);
    const reported = formatBuildsetLabel(reportedBuildset);

    return {
      kind: "pending",
      pending: {
        value: target.value,
        id: targetBuildset.id,
        tooltip: buildTooltip({ label: target }),
      },
      current: {
        value: reported.value,
        id: reportedBuildset.id,
        pinned: false,
        tooltip: buildTooltip({ label: reported }),
      },
    };
  }

  const buildset = (reportedBuildset ?? targetBuildset)!;
  const label = formatBuildsetLabel(buildset);
  const tooltip = buildTooltip({ label });

  // Unpinned machines: reported buildset equals channel target
  return {
    kind: "single",
    current: {
      value: label.value,
      id: buildset.id,
      pinned: false,
      tooltip,
    },
  };
}

/** Helpers */
const Line = (props: {
  canRead: boolean;
  to?: string;
  value: string;
  label: string;
  title?: string;
  isPending: boolean;
}) => {
  const { canRead, to, value, label, title, isPending } = props;
  const labelText = `${label}:`;

  const target =
    canRead && to ? (
      <div className={isPending ? "pending" : undefined}>
        <span className="label">{labelText}</span> <Link to={to}>{value}</Link>
      </div>
    ) : (
      <div className={isPending ? "pending" : undefined}>
        {labelText} {value}
      </div>
    );

  return renderWithTooltip(target as ReactElement, title);
};

const renderWithTooltip = (target: ReactElement, tooltip?: string) => {
  if (!tooltip) return target;
  return <BuildsetTooltip id={tooltip}>{target}</BuildsetTooltip>;
};

/** Main cell */
export const SoftwareVersionCell = ({
  rowData,
  userPermissions,
  channelNameToBuildset,
  buildsetIdMap,
}: {
  rowData: Machine;
  userPermissions: Permission[];
  channelNameToBuildset: ChannelNameToBuildset;
  buildsetIdMap: BuildsetIdMap;
}) => {
  const canReadSoftware = testPermissions("buildSets/read", userPermissions);

  const view = getBuildsetView({
    machine: rowData,
    channelNameToBuildset,
    buildsetIdMap,
  });

  if (view.kind === "empty") return <CustomCellStatic>--</CustomCellStatic>;

  if (view.kind === "single") {
    const { current } = view;
    const content = (
      <>
        {current.value}
        {current.pinned && ` (${t`common.pinned`})`}
      </>
    );

    const wrappedContent = current.tooltip ? (
      <BuildsetTooltip id={current.tooltip}>{content}</BuildsetTooltip>
    ) : (
      content
    );

    if (canReadSoftware) {
      return (
        <CustomCellLink to={adminUrls.buildset(current.id)}>
          {wrappedContent}
        </CustomCellLink>
      );
    }

    return <CustomCellStatic>{wrappedContent}</CustomCellStatic>;
  }

  // kind === "pending"
  const { current, pending } = view;

  const pendingUrl = pending.id ? adminUrls.buildset(pending.id) : undefined;
  const currentUrl =
    current.id && current.value !== null
      ? adminUrls.buildset(current.id)
      : undefined;

  return (
    <CustomCellStatic>
      <S.VersionWrapper>
        <Line
          canRead={canReadSoftware}
          to={pendingUrl}
          value={pending.value}
          label={t`common.pending`}
          title={pending.tooltip}
          isPending={true}
        />
        <Line
          canRead={canReadSoftware}
          to={currentUrl}
          value={current.value ?? "--"}
          label={t`common.current`}
          title={current.tooltip}
          isPending={false}
        />
      </S.VersionWrapper>
    </CustomCellStatic>
  );
};
